import { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { cx } from "../../utils/cx";
import styles from "./Thumbnail.module.scss";

export type ThumbnailVariant =
    | "product-icon"
    | "product-image"
    | "avatar-initials"
    | "avatar-image"
    | "icon";

export type ThumbnailSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
export type AvatarTone =
    | "blue"
    | "peach"
    | "brown"
    | "green"
    | "orange"
    | "purple"
    | "yellow";

export interface ThumbnailProps extends HTMLAttributes<HTMLDivElement> {
    variant: ThumbnailVariant;
    size?: ThumbnailSize;
    /** Image source for `product-image` and `avatar-image`. */
    src?: string;
    /** Alt text for image variants. */
    alt?: string;
    /** Initials text for `avatar-initials`. */
    initials?: string;
    /** Fill tone used by `avatar-initials`. */
    avatarTone?: AvatarTone;
    /** Icon content for `product-icon` and `icon` variants. */
    icon?: ReactNode;
    /**
     * `product-icon` / `icon` tile fill. `tinted` = brand green tint (default).
     * `raised` = white surface + hairline border (e.g. AppHeader store glyph).
     */
    iconSurface?: "tinted" | "raised";
    /** `product-icon` / `icon` — circular tile (e.g. header store selector). */
    circle?: boolean;
    /** Optional border for `product-image` variant. */
    bordered?: boolean;
}

type SizeMetric = {
    box: number;
    radius: number;
    icon: number;
    fontSize?: number;
    lineHeight?: number;
};

const PRODUCT_METRICS: Record<ThumbnailSize, SizeMetric> = {
    xs: { box: 32, radius: 8, icon: 18 },
    sm: { box: 48, radius: 12, icon: 24 },
    md: { box: 64, radius: 16, icon: 24 },
    lg: { box: 88, radius: 20, icon: 48 },
    xl: { box: 144, radius: 24, icon: 48 },
    "2xl": { box: 200, radius: 24, icon: 48 },
};

const AVATAR_METRICS: Record<
    Exclude<ThumbnailSize, "xl" | "2xl">,
    SizeMetric
> = {
    xs: { box: 36, radius: 999, icon: 0, fontSize: 17, lineHeight: 20 },
    sm: { box: 48, radius: 999, icon: 0, fontSize: 23, lineHeight: 24 },
    md: { box: 56, radius: 999, icon: 0, fontSize: 23, lineHeight: 24 },
    lg: { box: 64, radius: 999, icon: 0, fontSize: 26, lineHeight: 32 },
};

const ICON_METRICS: Record<Exclude<ThumbnailSize, "xl" | "2xl">, SizeMetric> = {
    xs: { box: 32, radius: 8, icon: 18 },
    sm: { box: 48, radius: 12, icon: 24 },
    md: { box: 64, radius: 16, icon: 24 },
    lg: { box: 88, radius: 20, icon: 48 },
};

const AVATAR_TONE_CLASS: Record<AvatarTone, string> = {
    blue: styles.toneBlue,
    peach: styles.tonePeach,
    brown: styles.toneBrown,
    green: styles.toneGreen,
    orange: styles.toneOrange,
    purple: styles.tonePurple,
    yellow: styles.toneYellow,
};

const asAvatarSize = (
    size: ThumbnailSize,
): Exclude<ThumbnailSize, "xl" | "2xl"> =>
    size === "xl" || size === "2xl" ? "lg" : size;

const ThumbnailIconFallback = () => (
    <svg aria-hidden fill="none" viewBox="0 0 24 24">
        <path
            d="M5.5 9.5 9.5 5.5l4.2 4.2L17.9 5.5 21 8.6V18H3V9.5h2.5Z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.7"
        />
        <circle cx="8" cy="12.5" fill="currentColor" r="1.2" />
    </svg>
);

/**
 * Treez DS — Thumbnail.
 *
 * Figma source: `13901:79223` (matrix with Product / Avatars / Icon rows).
 */
export const Thumbnail = ({
    variant,
    size = "sm",
    src,
    alt = "",
    initials = "AL",
    avatarTone = "green",
    icon,
    iconSurface = "tinted",
    circle = false,
    bordered = false,
    className,
    ...rest
}: ThumbnailProps) => {
    const metric =
        variant === "avatar-initials" || variant === "avatar-image"
            ? AVATAR_METRICS[asAvatarSize(size)]
            : variant === "icon"
            ? ICON_METRICS[asAvatarSize(size)]
            : PRODUCT_METRICS[size];

    const style = {
        width: `${metric.box}px`,
        height: `${metric.box}px`,
        minWidth: `${metric.box}px`,
        minHeight: `${metric.box}px`,
        borderRadius: `${metric.radius}px`,
    } as CSSProperties;

    if (variant === "product-image" || variant === "avatar-image") {
        return (
            <div
                className={cx(
                    styles.root,
                    styles.image,
                    bordered && styles.bordered,
                    variant === "avatar-image" && styles.circle,
                    className,
                )}
                data-ui="thumbnail"
                data-variant={variant}
                style={style}
                {...rest}
            >
                {src ? (
                    <img alt={alt} className={styles.imageContent} src={src} />
                ) : null}
            </div>
        );
    }

    if (variant === "avatar-initials") {
        return (
            <div
                className={cx(
                    styles.root,
                    styles.initials,
                    AVATAR_TONE_CLASS[avatarTone],
                    className,
                )}
                data-ui="thumbnail"
                data-variant={variant}
                style={style}
                {...rest}
            >
                <span
                    className={styles.initialsText}
                    style={{
                        fontSize: `${metric.fontSize}px`,
                        lineHeight: `${metric.lineHeight}px`,
                    }}
                >
                    {initials}
                </span>
            </div>
        );
    }

    return (
        <div
            className={cx(
                styles.root,
                iconSurface === "raised"
                    ? styles.iconSurfaceRaised
                    : styles.iconTile,
                circle && styles.circle,
                className,
            )}
            data-ui="thumbnail"
            data-variant={variant}
            style={style}
            {...rest}
        >
            <span
                className={styles.icon}
                style={{
                    width: `${metric.icon}px`,
                    height: `${metric.icon}px`,
                }}
            >
                {icon ?? <ThumbnailIconFallback />}
            </span>
        </div>
    );
};
